/**
 * RidesAdminService
 * Admin surface: dashboard, campuses, events visibility, drivers, verifications, bookings,
 * availability, disputes, lookups, notification log.
 * Route: rides/api/rides/admin (roles Admin,RidesAdmin)
 * Settings live in RidesSettingsService.
 */
import { BaseService } from '../base/BaseService';
import { unwrapRidesResponse as unwrap } from './unwrapRidesResponse';
/** Rides admin service (JWT + role). */
export class RidesAdminService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? RidesAdminService.DEFAULT_SERVICE_PATH,
            basePath: RidesAdminService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /** Flatten a RidesAdminListQuery into query-string params (undefined values are dropped). */
    listParams(query = {}) {
        return {
            page: query.page,
            pageSize: query.pageSize,
            search: query.search,
            statusID: query.statusID,
            eventID: query.eventID,
            locationID: query.locationID,
            typeID: query.typeID,
            from: query.from,
            to: query.to,
            includePast: query.includePast,
        };
    }
    // ---- dashboard ----
    /** GET api/rides/admin/dashboard */
    async getDashboard() {
        return unwrap(await this.getDashboardRaw(), 'Rides dashboard not found');
    }
    getDashboardRaw() {
        return this.http.get(this.buildUrl('dashboard'));
    }
    // ---- campuses ----
    /** GET api/rides/admin/campuses — every Location with its ride-campus state. */
    async getCampuses() {
        return (await this.getCampusesRaw()).data ?? [];
    }
    getCampusesRaw() {
        return this.http.get(this.buildUrl('campuses'));
    }
    /** PUT api/rides/admin/campuses/{locationId} — enable / configure a ride campus. */
    async upsertCampus(locationId, request) {
        return unwrap(await this.upsertCampusRaw(locationId, request), `Failed to save ride campus ${locationId}`);
    }
    upsertCampusRaw(locationId, request) {
        return this.http.put(this.buildUrl(`campuses/${locationId}`), request);
    }
    /**
     * DELETE api/rides/admin/campuses/{locationId} — hide the campus in Carpool. With `reset`, remove the Carpool
     * override instead so the location follows the Locations service and the campus settings again (254k).
     */
    async removeCampus(locationId, options) {
        const response = await this.removeCampusRaw(locationId, options);
        return response.data ?? response.success;
    }
    removeCampusRaw(locationId, options) {
        return this.http.delete(options?.reset ? this.buildUrlWithParams(`campuses/${locationId}`, { reset: true }) : this.buildUrl(`campuses/${locationId}`));
    }
    /** POST api/rides/admin/campuses/sync — refresh names/addresses/service times from LocationManagement. */
    async syncCampuses() {
        return (await this.syncCampusesRaw()).data ?? [];
    }
    syncCampusesRaw() {
        return this.http.post(this.buildUrl('campuses/sync'), {});
    }
    // ---- events ----
    /** GET api/rides/admin/events?from&to — calendar (all events, with showInRides). */
    async getEvents(query = {}) {
        return (await this.getEventsRaw(query)).data ?? [];
    }
    getEventsRaw(query = {}) {
        return this.http.get(this.buildUrlWithParams('events', { from: query.from, to: query.to }));
    }
    /** PUT api/rides/admin/events/{eventId}/rides-visibility — toggle showInRides (proxied to EventManagement). */
    async setEventRidesVisibility(eventId, request) {
        return unwrap(await this.setEventRidesVisibilityRaw(eventId, request), `Failed to update event ${eventId}`);
    }
    setEventRidesVisibilityRaw(eventId, request) {
        return this.http.put(this.buildUrl(`events/${eventId}/rides-visibility`), request);
    }
    // ---- drivers ----
    /** GET api/rides/admin/drivers (paged; search/statusID/locationID). */
    async getDrivers(query = {}) {
        return unwrap(await this.getDriversRaw(query), 'Failed to list drivers');
    }
    getDriversRaw(query = {}) {
        return this.http.get(this.buildUrlWithParams('drivers', this.listParams(query)));
    }
    /** GET api/rides/admin/drivers/{userId} */
    async getDriver(userId) {
        return unwrap(await this.getDriverRaw(userId), `Driver ${userId} not found`);
    }
    getDriverRaw(userId) {
        return this.http.get(this.buildUrl(`drivers/${encodeURIComponent(userId)}`));
    }
    /** POST api/rides/admin/drivers/{userId}/suspend — closes open listings. */
    async suspendDriver(userId, request) {
        return unwrap(await this.suspendDriverRaw(userId, request), `Failed to suspend driver ${userId}`);
    }
    suspendDriverRaw(userId, request) {
        return this.http.post(this.buildUrl(`drivers/${encodeURIComponent(userId)}/suspend`), request);
    }
    /** POST api/rides/admin/drivers/{userId}/unsuspend */
    async unsuspendDriver(userId) {
        return unwrap(await this.unsuspendDriverRaw(userId), `Failed to unsuspend driver ${userId}`);
    }
    unsuspendDriverRaw(userId) {
        return this.http.post(this.buildUrl(`drivers/${encodeURIComponent(userId)}/unsuspend`), {});
    }
    // ---- verifications ----
    /** GET api/rides/admin/verifications (paged; statusID/typeID/search). */
    async getVerifications(query = {}) {
        return unwrap(await this.getVerificationsRaw(query), 'Failed to list verifications');
    }
    getVerificationsRaw(query = {}) {
        return this.http.get(this.buildUrlWithParams('verifications', this.listParams(query)));
    }
    /** POST api/rides/admin/verifications/{userId}/{typeId}/record — manual provider outcome. */
    async recordVerification(userId, typeId, request) {
        return unwrap(await this.recordVerificationRaw(userId, typeId, request), 'Failed to record verification');
    }
    recordVerificationRaw(userId, typeId, request) {
        return this.http.post(this.buildUrl(`verifications/${encodeURIComponent(userId)}/${typeId}/record`), request);
    }
    /** POST api/rides/admin/verifications/{userId}/{typeId}/revoke — closes open listings when a driver loses verification. */
    async revokeVerification(userId, typeId, request = {}) {
        return unwrap(await this.revokeVerificationRaw(userId, typeId, request), 'Failed to revoke verification');
    }
    revokeVerificationRaw(userId, typeId, request = {}) {
        return this.http.post(this.buildUrl(`verifications/${encodeURIComponent(userId)}/${typeId}/revoke`), request);
    }
    // ---- bookings ----
    /** GET api/rides/admin/bookings (paged; statusID/eventID/locationID/from/to/includePast). */
    async getBookings(query = {}) {
        return unwrap(await this.getBookingsRaw(query), 'Failed to list bookings');
    }
    getBookingsRaw(query = {}) {
        return this.http.get(this.buildUrlWithParams('bookings', this.listParams(query)));
    }
    /** GET api/rides/admin/bookings/{id} */
    async getBooking(id) {
        return unwrap(await this.getBookingRaw(id), `Booking ${id} not found`);
    }
    getBookingRaw(id) {
        return this.http.get(this.buildUrl(`bookings/${id}`));
    }
    /** POST api/rides/admin/bookings/{id}/cancel */
    async cancelBooking(id, request = {}) {
        return unwrap(await this.cancelBookingRaw(id, request), `Failed to cancel booking ${id}`);
    }
    cancelBookingRaw(id, request = {}) {
        return this.http.post(this.buildUrl(`bookings/${id}/cancel`), request);
    }
    // ---- availability ----
    /** GET api/rides/admin/availability (paged; statusID/eventID/locationID/from/to/includePast). */
    async getAvailability(query = {}) {
        return unwrap(await this.getAvailabilityRaw(query), 'Failed to list ride listings');
    }
    getAvailabilityRaw(query = {}) {
        return this.http.get(this.buildUrlWithParams('availability', this.listParams(query)));
    }
    /** POST api/rides/admin/availability/{id}/cancel — cancels the listing and its bookings. */
    async cancelAvailability(id, request = {}) {
        return unwrap(await this.cancelAvailabilityRaw(id, request), `Failed to cancel ride listing ${id}`);
    }
    cancelAvailabilityRaw(id, request = {}) {
        return this.http.post(this.buildUrl(`availability/${id}/cancel`), request);
    }
    // ---- disputes ----
    /** GET api/rides/admin/disputes (paged; statusID). */
    async getDisputes(query = {}) {
        return unwrap(await this.getDisputesRaw(query), 'Failed to list disputes');
    }
    getDisputesRaw(query = {}) {
        return this.http.get(this.buildUrlWithParams('disputes', this.listParams(query)));
    }
    /** POST api/rides/admin/disputes/{id}/resolve */
    async resolveDispute(id, request) {
        return unwrap(await this.resolveDisputeRaw(id, request), `Failed to resolve dispute ${id}`);
    }
    resolveDisputeRaw(id, request) {
        return this.http.post(this.buildUrl(`disputes/${id}/resolve`), request);
    }
    // ---- lookups ----
    /** GET api/rides/admin/lookups/{table} — rows of one lookup table (incl. inactive). */
    async getLookup(table) {
        return (await this.getLookupRaw(table)).data ?? [];
    }
    getLookupRaw(table) {
        return this.http.get(this.buildUrl(`lookups/${encodeURIComponent(table)}`));
    }
    /** PUT api/rides/admin/lookups/{table}/{id} — rename / recolour / (de)activate a lookup row. */
    async updateLookup(table, id, item) {
        return unwrap(await this.updateLookupRaw(table, id, item), `Failed to update ${table} ${id}`);
    }
    updateLookupRaw(table, id, item) {
        return this.http.put(this.buildUrl(`lookups/${encodeURIComponent(table)}/${id}`), item);
    }
    // ---- notifications ----
    /** GET api/rides/admin/notifications/log (paged; search/from/to). */
    async getNotificationLog(query = {}) {
        return unwrap(await this.getNotificationLogRaw(query), 'Failed to list notification log');
    }
    getNotificationLogRaw(query = {}) {
        return this.http.get(this.buildUrlWithParams('notifications/log', this.listParams(query)));
    }
    // ---- documents (254g uploads) ----
    /** GET api/rides/admin/documents — review queue / search (Rides.Verification.Read). */
    async listDocuments(query = {}) {
        return unwrap(await this.listDocumentsRaw(query), 'Documents not found');
    }
    listDocumentsRaw(query = {}) {
        return this.http.get(this.buildUrlWithParams('documents', {
            page: query.page,
            pageSize: query.pageSize,
            search: query.search,
            statusID: query.statusID,
            uploadTypeID: query.uploadTypeID,
            userId: query.userId,
            hasFile: query.hasFile,
        }));
    }
    /** GET api/rides/admin/users/{userId}/documents — every document of one member (driver detail). */
    async getUserDocuments(userId) {
        return (await this.getUserDocumentsRaw(userId)).data ?? [];
    }
    getUserDocumentsRaw(userId) {
        return this.http.get(this.buildUrl(`users/${encodeURIComponent(userId)}/documents`));
    }
    /** GET api/rides/admin/documents/{id}/view — short-lived signed link (logged). Never cache it. */
    async getDocumentLink(rideUploadId) {
        return unwrap(await this.getDocumentLinkRaw(rideUploadId), 'Document not available');
    }
    getDocumentLinkRaw(rideUploadId) {
        return this.http.get(this.buildUrl(`documents/${rideUploadId}/view`));
    }
    /** POST api/rides/admin/documents/{id}/review — accept / reject (Rides.Verification.Record). */
    async reviewDocument(rideUploadId, request) {
        return unwrap(await this.reviewDocumentRaw(rideUploadId, request), 'Failed to review document');
    }
    reviewDocumentRaw(rideUploadId, request) {
        return this.http.post(this.buildUrl(`documents/${rideUploadId}/review`), request);
    }
    /** GET api/rides/admin/documents/{id}/log — audit trail of one document. */
    async getDocumentLog(rideUploadId) {
        return (await this.getDocumentLogRaw(rideUploadId)).data ?? [];
    }
    getDocumentLogRaw(rideUploadId) {
        return this.http.get(this.buildUrl(`documents/${rideUploadId}/log`));
    }
    /** GET api/rides/admin/document-types — every document type, including inactive ones. */
    async getDocumentTypes() {
        return (await this.getDocumentTypesRaw()).data ?? [];
    }
    getDocumentTypesRaw() {
        return this.http.get(this.buildUrl('document-types'));
    }
    /** POST api/rides/admin/document-types — add a document type (Rides.Settings.Manage). */
    async createDocumentType(request) {
        return unwrap(await this.createDocumentTypeRaw(request), 'Failed to create document type');
    }
    createDocumentTypeRaw(request) {
        return this.http.post(this.buildUrl('document-types'), request);
    }
    /** PUT api/rides/admin/document-types/{id} — patch requirement levels, help text, flags (Rides.Settings.Manage). */
    async updateDocumentType(uploadTypeId, request) {
        return unwrap(await this.updateDocumentTypeRaw(uploadTypeId, request), 'Failed to update document type');
    }
    updateDocumentTypeRaw(uploadTypeId, request) {
        return this.http.put(this.buildUrl(`document-types/${uploadTypeId}`), request);
    }
}
RidesAdminService.DEFAULT_SERVICE_PATH = 'rides';
RidesAdminService.BASE_PATH = 'api/rides/admin';
//# sourceMappingURL=RidesAdminService.js.map